import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { DEFAULT_RETENTION_DAYS, DOCUMENT_KIND_LABELS } from "@titleverify/shared";
import {
  apiErrorText,
  apiFetch,
  apiUpload,
  type CaseDocument,
  type CaseDocumentList,
  type SignedDocumentUrl,
} from "../api.js";
import { useAuth } from "../auth.js";
import { LoadFailure } from "./LoadFailure.js";

/**
 * CUSTODY, SHOWN TO THE PEOPLE WHOSE CASE IT IS.
 * ===========================================================================
 *
 * This is the surface behind the landing page's promise that a client can "add
 * the deed or plan papers you already hold". Until it existed the promise was
 * false, which was a data-honesty failure of our own making rather than one
 * inherited from a registry.
 *
 * It is mounted for two viewers, and the difference is not cosmetic:
 *
 *  - the CLIENT, on their own case. `document:read` grants them every kind on
 *    their own case, so what they see is the whole of what the platform holds.
 *  - the assigned PROFESSIONAL. Also every kind, so they see the client's own
 *    uploads as well as registry extracts.
 *
 * It is deliberately NOT mounted for the two roles the server filters:
 *
 *  - a SEARCH AGENT is granted `extract` and nothing else, precisely so registry
 *    legwork never exposes a client's identity scans (COMPLIANCE Constraint 4).
 *    An agent's empty list would therefore mean "no extracts visible to you" and
 *    NOT "the client added nothing" — and there is no agent surface yet anyway.
 *  - an ADMIN has no `document:read` rule at all (ARCHITECTURE §9 keeps staff
 *    out of client documents; access is break-glass and logged, and that path
 *    does not exist yet). An admin therefore receives an empty list for EVERY
 *    case, always. Rendering this panel on an admin screen would print "no
 *    documents" over every case in the system, which is a fabricated fact about
 *    every one of them.
 *
 * WHAT AN EMPTY LIST ACTUALLY PROVES, as the server behaves TODAY.
 * `listCaseDocuments` (apps/api/src/documents/service.ts) now runs TWO gates. It
 * first asks the chokepoint for `case:read` and, if the answer is no, throws the
 * same NotFoundError("Case") as an id that was never issued — so a stranger
 * naming a real case id gets 404, not the 200-and-empty-array this comment used
 * to describe. That earlier claim was true when this file was written and is now
 * false; it is corrected here rather than left to mislead the next editor.
 *
 * The trap did not disappear, it MOVED to the second gate. Passing `case:read`
 * still says nothing about `document:read`, which is filtered per document. An
 * admin holds `case:read` on every case and `document:read` on none, so an admin
 * receives 200 and an empty array for every case in the system — which is the
 * reason given above for never mounting this panel on an admin screen. An empty
 * list is evidence about a case only for a viewer granted every kind on it, which
 * is exactly the two viewers this component accepts.
 */

/**
 * The server's own limits, mirrored here as a COURTESY CHECK ONLY.
 *
 * The authority is apps/api/src/documents/service.ts (ALLOWED_CONTENT_TYPES and
 * MAX_BYTES). Nothing in this file enforces anything — the server refuses, and
 * these values exist so a user learns about a limit before waiting out an upload
 * rather than after.
 *
 * The size check earns its place for a specific reason: a body over the parser's
 * 25mb ceiling is rejected by express.raw, whose `entity.too.large` is not
 * handled in the API's error mapper, so over the wire an oversized file returns
 * a bare 500 rather than the honest "Files must be under 25 MB." Checking here
 * is the only way the user currently gets a true reason. That is worth fixing on
 * the API side; it is not fixable from a browser.
 */
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/tiff"];
const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/** Presentation for the content type the server recorded. Not a vocabulary anyone rules on. */
const FILE_TYPE_LABEL: Record<string, string> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPEG scan",
  "image/png": "PNG scan",
  "image/tiff": "TIFF scan",
};

type Viewer = "client" | "professional";

export function CaseDocuments({ caseId, viewer }: { caseId: string; viewer: Viewer }) {
  const { getToken } = useAuth();

  const documents = useQuery({
    queryKey: ["documents", caseId],
    queryFn: async () =>
      apiFetch<CaseDocumentList>(`/cases/${caseId}/documents`, { token: await getToken() }),
  });

  return (
    <section className="stack">
      <h3>Papers held on this request</h3>

      {documents.isPending ? <p className="muted" role="status">Loading what is held…</p> : null}

      {documents.error ? (
        <LoadFailure error={documents.error} fallback="Could not load the documents.">
          <p>
            <small>
              Nothing held on this request has been affected. This is the list of papers
              failing to load, not the papers themselves.
            </small>
          </p>
        </LoadFailure>
      ) : null}

      {documents.data ? (
        documents.data.documents.length === 0 ? (
          <EmptyCustody viewer={viewer} />
        ) : (
          <DocumentTable documents={documents.data.documents} />
        )
      ) : null}

      <AddPapers caseId={caseId} viewer={viewer} />
    </section>
  );
}

/**
 * Nothing held — said in the terms of what THIS viewer's list actually proves.
 *
 * Both viewers here are granted every kind on this case, so "nothing is held" is
 * genuinely what an empty list means for them. That is why this component is not
 * given to a search agent or an admin: for them the same empty array means
 * something entirely different, and the same sentence would be a lie.
 */
function EmptyCustody({ viewer }: { viewer: Viewer }) {
  return (
    <p className="empty">
      {viewer === "client"
        ? "Nothing has been added to this request yet — not by you, and not by anyone working on " +
          "it. Adding your deed or plan papers is optional and the request is not held up " +
          "waiting for them. If you do have something to hand, the panel below is where it " +
          "goes; a photograph of a deed is fine."
        : "This case holds no documents. You see every document on a case you are assigned to, " +
          "including the client's own uploads, so this also means the client has not added " +
          "papers yet."}
    </p>
  );
}

function DocumentTable({ documents }: { documents: CaseDocument[] }) {
  // The API applies no ordering (`findMany` with no orderBy), so the order it
  // returns is whatever Postgres felt like. Sorted here rather than trusted.
  const ordered = [...documents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <>
      <div className="table-wrap" tabIndex={0} role="region" aria-label="Documents held on this case">
        <table>
          <thead>
            <tr>
              <th>What it is</th>
              <th>Added</th>
              <th>File type</th>
              <th>Size</th>
              <th>Held until</th>
              <th>Fingerprint (SHA-256)</th>
              <th>
                <span className="visually-hidden">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((doc) => (
              <DocumentRow key={doc.id} doc={doc} />
            ))}
          </tbody>
        </table>
      </div>

      {/*
        .caveat — the platform's own qualification of its own numbers, which is
        exactly what this is. Two things have to be said and neither may be
        rounded off:

        The date is real: `retention_expires_at` is NOT NULL with no default, set
        at the moment of upload, and a database trigger lets it move EARLIER only.

        The deletion is not yet automatic. There is no retention job in this
        repository — nothing sweeps expired documents and nothing writes
        `deleted_at`. So a past date does not mean a file is gone, and this page
        will not say one is gone on the strength of a date. Only the server's own
        `deleted` flag licenses that sentence, and it is rendered per row.
      */}
      <p className="caveat">
        The date each document is held until was fixed when it was uploaded, and can be brought
        forward but never extended. It is when the document becomes due for deletion, not a record
        that anything has happened to it — a document is shown here as deleted only when the
        service reports it deleted.
      </p>

      {/*
        Said once, plainly, rather than left for someone to notice: there is no
        name column because there is no name anywhere in the system to put in it.
      */}
      <p className="muted">
        Documents have no filenames here. The name a file had on your computer is never sent to us
        and never stored — it usually contains somebody's name, and storage keys and logs cannot be
        edited afterwards. The fingerprint is what identifies a document exactly.
      </p>
    </>
  );
}

function DocumentRow({ doc }: { doc: CaseDocument }) {
  return (
    <tr>
      {/*
        The kind, as a role-level statement about who added it, from the shared
        label map. PLAIN TEXT, not a .status-* badge: those colours are a
        judgement about whether a person's enrolment has been proven, and what a
        document is is neither a verdict nor a workflow state.
      */}
      <td>{DOCUMENT_KIND_LABELS[doc.kind]}</td>
      <td>{new Date(doc.createdAt).toLocaleString()}</td>
      {/* Nulls are possible on both of these columns and say so rather than sitting blank. */}
      <td>
        {doc.contentType === null ? (
          <span className="muted">not recorded</span>
        ) : (
          (FILE_TYPE_LABEL[doc.contentType] ?? doc.contentType)
        )}
      </td>
      <td>
        {doc.sizeBytes === null ? <span className="muted">not recorded</span> : fileSize(doc.sizeBytes)}
      </td>
      <td>{new Date(doc.retentionExpiresAt).toLocaleDateString()}</td>
      <td>
        <code>{doc.sha256}</code>
      </td>
      <td>
        <OpenDocument doc={doc} />
      </td>
    </tr>
  );
}

/**
 * Getting at the bytes: a short-lived signed URL, minted on the click.
 *
 * NOT a TanStack query, and the reason is not stylistic. The URL lives five
 * minutes and the response carries `Cache-Control: no-store`, because a signed
 * URL sitting in a shared cache is the public object the storage design forbids,
 * reached by another route. Any `useQuery` with any staleTime would put it back
 * in a cache. So this is a plain handler: the URL is fetched, handed to a new
 * tab, and goes out of scope.
 *
 * The one place it is retained is the popup-blocked fallback below, where the
 * user has to be given something to click. That is a deliberate, visible
 * exception with the expiry stated on it.
 */
function OpenDocument({ doc }: { doc: CaseDocument }) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fallback, setFallback] = useState<SignedDocumentUrl | null>(null);

  /*
    A deleted document has no bytes to serve, and the server answers a request
    for one with a 400 explaining exactly that. Rendering a button that can only
    fail is worse than rendering none, so the row states the fact instead.

    .decided, not [role="alert"]: a document deleted at the end of its retention
    period is the platform keeping a promise, not something going wrong. The
    metadata row survives the object on purpose (COMPLIANCE Constraint 4) so that
    a client can see it was held and then destroyed, rather than find a silence.
  */
  if (doc.deleted) {
    return (
      <p className="decided">
        Deleted. The file itself was destroyed; this record that we held it remains.
      </p>
    );
  }

  async function open() {
    setBusy(true);
    setFailure(null);
    setFallback(null);
    try {
      const issued = await apiFetch<SignedDocumentUrl>(`/documents/${doc.id}/url`, {
        token: await getToken(),
      });
      const opened = window.open(issued.url, "_blank", "noopener,noreferrer");
      if (opened === null) setFallback(issued);
    } catch (error) {
      // A 400 here is the server saying the document was deleted between this
      // page loading and this click. Its wording is specific and correct and is
      // rendered as it came.
      setFailure(apiErrorText(error, "Could not open this document."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/*
        No .actions wrapper: that class is a row of buttons at the END OF A FORM
        or panel and carries the margin to match. This is a single control inside
        a table cell.
      */}
      <button type="button" className="quiet compact" disabled={busy} onClick={() => void open()}>
        {busy ? "Getting a link…" : "Open"}
      </button>

      {failure ? <p role="alert">{failure}</p> : null}

      {fallback ? (
        // Neutral, not an alert: nothing failed, the browser simply declined to
        // open a tab. The expiry is the server's own number, not a constant
        // restated here.
        <p className="notice">
          Your browser blocked the new tab.{" "}
          <a href={fallback.url} target="_blank" rel="noopener noreferrer">
            Open the document
          </a>{" "}
          — this link stops working {fallback.expiresInSeconds} seconds after it was issued, and a
          new one is needed after that.
        </p>
      ) : null}
    </>
  );
}

/**
 * Adding papers.
 *
 * WHAT IS NOT OFFERED HERE. There is no control that sets what a document is:
 * the server derives `kind` from the uploader's role and refuses to read it from
 * the request, because a client able to label their identity scan `extract`
 * would have published it to a search agent. Offering a picker would imply a
 * control that does not exist and would be asking a question whose answer is
 * discarded.
 *
 * There is also no delete, no replace and no rename, because the API has none —
 * the custody columns are frozen after insert by migration 0006. A control that
 * can only 404 is worse than an absent one, so the consequence panel says
 * plainly that adding is one-way rather than leaving the user to discover it.
 */
function AddPapers({ caseId, viewer }: { caseId: string; viewer: Viewer }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [chosen, setChosen] = useState<File[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [sending, setSending] = useState(false);

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    setChosen(Array.from(event.target.files ?? []));
    setOutcomes([]);
  }

  function mark(index: number, outcome: Outcome) {
    setOutcomes((current) => current.map((existing, i) => (i === index ? outcome : existing)));
  }

  /**
   * One request per file, in sequence, each reported on its own.
   *
   * SEQUENTIAL because each request carries a whole file in its body; firing
   * five 25 MB uploads at once on a Sri Lankan mobile connection helps nobody.
   *
   * PER FILE because a partial failure has to read as a partial failure. Three
   * stored and two refused is the truth in that case, and a single "upload
   * failed" or a single tick would each be a different lie about it.
   */
  async function send() {
    setSending(true);
    setOutcomes(chosen.map((file) => ({ state: "waiting", name: file.name })));

    /*
      A FILE THE SERVICE DID NOT TAKE STAYS SELECTED.
      ---------------------------------------------------------------------
      This used to clear the whole selection the moment the loop ended, no
      matter what the loop had found. A dropped connection halfway through
      therefore threw away every file the person had picked, and someone
      photographing deeds on a phone had to walk back through the picker and
      find them all again to try the same thing twice.

      So the loop keeps the files that did NOT get through, and only those.
      What was stored is dropped, because re-sending it would store a second
      copy — the API has no delete, so a duplicate is permanent.

      Every not-stored file is kept, including the ones this page refused
      locally for type or size. Deciding which failures are worth retrying
      would mean this page ruling on whether a refusal is permanent, and it
      does not know that; the reason it was given is printed against the file
      and the person can decide.
    */
    const notStored: File[] = [];

    for (let index = 0; index < chosen.length; index += 1) {
      const file = chosen[index]!;
      mark(index, { state: "sending", name: file.name });

      const refusal = localRefusal(file);
      if (refusal !== null) {
        mark(index, { state: "refused", name: file.name, reason: refusal });
        notStored.push(file);
        continue;
      }

      try {
        const stored = await apiUpload<CaseDocument>(`/cases/${caseId}/documents`, {
          token: await getToken(),
          file,
        });
        mark(index, { state: "stored", document: stored });
      } catch (error) {
        mark(index, {
          state: "refused",
          name: file.name,
          reason: apiErrorText(error, "This file was not stored."),
        });
        notStored.push(file);
      }
    }

    setSending(false);
    setChosen(notStored);
    keepInPicker(input.current, notStored);
    void queryClient.invalidateQueries({ queryKey: ["documents", caseId] });
  }

  function discard() {
    setChosen([]);
    setOutcomes([]);
    if (input.current) input.current.value = "";
  }

  const stored = outcomes.filter((o) => o.state === "stored");
  const refused = outcomes.filter((o) => o.state === "refused");
  const inFlight = outcomes.filter((o) => o.state === "waiting" || o.state === "sending");

  /*
    "These files are what an attempt left behind", derived rather than stored
    in a second piece of state that could disagree with the first. There are
    outcomes from a finished attempt AND files still selected only when send()
    put back what it could not store; picking new files clears the outcomes.
  */
  const retained = !sending && outcomes.length > 0 && chosen.length > 0;

  return (
    // .consequence: what adding a document actually does, said before it is
    // done. It starts a retention clock, and it cannot be undone from here.
    <div className="consequence">
      <h3>{viewer === "client" ? "Add your deed or plan papers" : "Add a registry extract"}</h3>

      <p>
        {viewer === "client"
          ? "Anything you add is recorded as papers the client supplied. There is no setting for " +
            "what a document is — the platform works it out from who added it, and neither you " +
            "nor we can label it as something else."
          : "Anything you add is recorded as retrieved at the registry, because that is what an " +
            "assigned professional's upload is. There is no setting for it and no way to add a " +
            "document on the client's behalf."}
      </p>

      <p>
        <small>
          Adding a document cannot be undone from this page: there is no delete, no replace and no
          rename here, because the platform has none — a document's record is fixed once it is
          taken into custody. If something is wrong with what you sent, add the correct one; both
          will be held. The platform keeps a document for {DEFAULT_RETENTION_DAYS} days from the
          moment it is stored, and the exact date appears against it in the list above.
        </small>
      </p>

      <div className="field">
        <label htmlFor={`papers-${caseId}`}>Choose one or more files</label>
        <input
          id={`papers-${caseId}`}
          ref={input}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(",")}
          disabled={sending}
          onChange={onPick}
        />
        {/*
          The limits BEFORE a file is picked, so a refusal is never a surprise.
          Phrased as the service's rules, which is what they are — this page
          checks them early as a courtesy, and the service is what refuses.
        */}
        <small className="field-hint">
          PDF, JPEG, PNG or TIFF, each under {MAX_UPLOAD_MB} MB. These are the service&rsquo;s
          limits; this page checks them before sending so that you get a real reason rather than a
          failed upload.
        </small>
      </div>

      {/*
        Progress, in the only terms that are true. `fetch` reports nothing about
        how many bytes have gone, so there is no percentage and no bar here — a
        moving bar we cannot source would be an invented fact about the upload.
        What is known is which file is being sent and which have finished.
      */}
      <div aria-live="polite">
        {sending ? (
          <p className="notice">
            Sending {stored.length + refused.length + 1} of {outcomes.length}. Large scans on a slow
            connection take a while, and there is no way to tell how far along one is — please
            leave this page open.
          </p>
        ) : null}

        {stored.length > 0 ? (
          <div className="notice">
            <p>
              <strong>
                {stored.length === 1 ? "One document was stored." : `${stored.length} documents were stored.`}
              </strong>{" "}
              They are in the list above.
            </p>
            <ul>
              {stored.map((outcome) =>
                outcome.state === "stored" ? (
                  <li key={outcome.document.id}>
                    {/*
                      Deliberately NOT the filename the user chose. It was never
                      sent, so echoing it after the response would show something
                      the platform does not have and invite someone to persist
                      it. What is shown is what the service actually recorded.
                    */}
                    {DOCUMENT_KIND_LABELS[outcome.document.kind]} —{" "}
                    {outcome.document.sizeBytes === null
                      ? "size not recorded"
                      : fileSize(outcome.document.sizeBytes)}
                    , held until {new Date(outcome.document.retentionExpiresAt).toLocaleDateString()}
                  </li>
                ) : null,
              )}
            </ul>
            <p>
              <small>
                The names these files had on your computer are not in that list because they were
                never sent to us.
              </small>
            </p>
          </div>
        ) : null}

        {refused.length > 0 ? (
          // A genuine failure for these files, and it does not get softened by
          // the successes above it. Each one names the file so the person knows
          // which to try again — that name is local to this browser and goes no
          // further than this list.
          <div role="alert">
            <p>
              <strong>
                {refused.length === 1
                  ? "One file was not stored."
                  : `${refused.length} files were not stored.`}
              </strong>{" "}
              Nothing was kept for these, and they are not in the list above.
            </p>
            {/*
              THE WAY OUT, MOVED ABOVE THE REASONS.
              It was the last line in this panel, under a list that can run to
              several files — so the recovery a person most needs ("your files
              are still here, press it again") sat below the part that reads like
              a list of what they got wrong. The facts are identical; the order
              is not, and on a phone the order is most of the message.
            */}
            {retained ? (
              <p>
                <strong>
                  {refused.length === 1 ? "This file is" : "These files are"} still selected.
                </strong>{" "}
                You can send {refused.length === 1 ? "it" : "them"} again without choosing{" "}
                {refused.length === 1 ? "it" : "them"} from your device a second time.
              </p>
            ) : null}
            <p>
              <small>
                Each reason below is the exact one the file was refused for — a type or a size
                the service does not take, or a request that did not complete. Adding papers is
                optional, so a request is not held up by one that will not go.
              </small>
            </p>
            <ul>
              {refused.map((outcome, index) =>
                outcome.state === "refused" ? (
                  <li key={`${outcome.name}-${index}`}>
                    {/*
                      <code>, because a filename is a literal and this one is
                      the user's, so it can be any length and contain no spaces
                      at all — "WhatsApp_Image_2026-07-19_at_10.32.11_deed.pdf"
                      is what a phone actually produces. As bold prose it does
                      not wrap and pushes the reason off the side of the screen
                      at 375px. <code> carries the stylesheet's overflow-wrap,
                      and <strong> keeps the emphasis that made it scannable.
                    */}
                    <strong>
                      <code>{outcome.name}</code>
                    </strong>{" "}
                    — {outcome.reason}
                  </li>
                ) : null,
              )}
            </ul>
            {/*
              Said explicitly above the reasons rather than after them, because
              the alternative is the person assuming their selection is gone and
              going back to the picker. Only these files are still selected —
              anything stored was taken out, so pressing the button again cannot
              store a second copy of it.
            */}
          </div>
        ) : null}
      </div>

      <div className="actions">
        <button type="button" disabled={sending || chosen.length === 0} onClick={() => void send()}>
          {sendLabel(sending, retained, chosen.length)}
        </button>
        {/*
          When files were put back, this is the only way to get to nothing
          selected, so it says what it throws away rather than "clear". When
          nothing was put back it clears the report of a finished attempt and
          nothing else.
        */}
        {retained ? (
          <button type="button" className="quiet" onClick={discard}>
            {chosen.length > 1 ? `Discard these ${chosen.length} files` : "Discard this file"}
          </button>
        ) : outcomes.length > 0 && inFlight.length === 0 ? (
          <button type="button" className="quiet" onClick={() => setOutcomes([])}>
            Clear this list
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What a file is at each moment. `stored` deliberately carries the SERVER's
 * record and drops the local filename, so the name visibly does not survive the
 * response — the type makes it impossible to render one for a stored document.
 */
type Outcome =
  | { state: "waiting"; name: string }
  | { state: "sending"; name: string }
  | { state: "stored"; document: CaseDocument }
  | { state: "refused"; name: string; reason: ReactNode };

/** The button says which of the two things pressing it does. */
function sendLabel(sending: boolean, retained: boolean, count: number): string {
  if (sending) return "Sending…";
  if (retained) return count > 1 ? `Try these ${count} again` : "Try this file again";
  return count > 1 ? `Add these ${count} files` : "Add this file";
}

/**
 * Put the not-stored files back into the file input itself, so the picker's own
 * label agrees with what this page says is selected instead of contradicting it.
 *
 * The input has to be rewritten rather than simply left alone: it still holds
 * the ORIGINAL selection, stored files included, and leaving that in place would
 * have the browser reporting more files than will actually be sent.
 *
 * Clearing it is what happens when everything got through — necessary, because
 * an input whose value is unchanged fires no `change` event when the same file
 * is picked again, and the person would then be unable to re-add a file at all.
 */
function keepInPicker(element: HTMLInputElement | null, keep: File[]) {
  if (element === null) return;
  if (keep.length === 0) {
    element.value = "";
    return;
  }
  try {
    const transfer = new DataTransfer();
    for (const file of keep) transfer.items.add(file);
    element.files = transfer.files;
  } catch {
    /*
      A browser without a constructible DataTransfer. The picker's own label
      goes stale, which is a cosmetic wrong; `chosen` still holds exactly the
      files that did not get through and the button still sends exactly those.
      Deliberately NOT falling back to clearing the input — that would take the
      person's files away, which is the failure this whole path exists to stop.
    */
  }
}

/**
 * The courtesy check. Mirrors the service; enforces nothing. See ACCEPTED_TYPES above.
 *
 * A NODE, NOT A STRING, for one reason. A content type is a single unbreakable
 * token, and a real one — application/vnd.openxmlformats-officedocument.
 * wordprocessingml.document, what a phone reports for a Word file — is wider
 * than a 375px screen. As prose it does not wrap: it runs off the side of the
 * panel taking the rest of the sentence with it, so the person cannot read why
 * their file was refused and cannot scroll to it either. In <code> it is both
 * semantically right and covered by the stylesheet's `code { overflow-wrap:
 * anywhere }`, which is the design system's existing answer to exactly this.
 */
function localRefusal(file: File): ReactNode | null {
  if (file.size === 0) {
    return "The file is empty — there are no bytes in it to store.";
  }
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return (
      <>
        This app cannot send{" "}
        {file.type === "" ? (
          "a file whose type your browser did not report"
        ) : (
          <>
            a <code>{file.type}</code>
          </>
        )}{" "}
        — the service takes PDF, JPEG, PNG and TIFF.
      </>
    );
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return (
      `At ${fileSize(file.size)} this is over the ${MAX_UPLOAD_MB} MB the service accepts, so it ` +
      `was not sent.`
    );
  }
  return null;
}

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
